// Admin invite — email-domain allowlist.
//
// Single source of truth for which corporate email domains are allowed
// to receive admin-portal invitations. Imported by BOTH the admin SPA
// (for form validation + dynamic copy) and the API users handler (for
// defense-in-depth enforcement on POST /users/invite).
//
// Keeping the list here — not as an env var — is deliberate:
//   - Two domains, hand-maintained, audited in source control.
//   - Adding a partner firm = a one-line diff that flows through code
//     review + the gitlab MR pipeline.
//   - No env-var drift risk between staging and prod.
//
// The admin pool is invite-only (see memory: cognito-portal-only +
// user-lifecycle-per-pool). This allowlist guards WHO can be invited;
// the role + permission system guards WHAT each invitee can do.

export const ADMIN_INVITE_ALLOWED_DOMAINS = [
  'gc-coach.ai',
  'daytwo-advisory.com',
] as const;

export type AdminInviteAllowedDomain = (typeof ADMIN_INVITE_ALLOWED_DOMAINS)[number];

/**
 * Returns true if the email's domain is in the allowlist. Case-insensitive,
 * tolerates surrounding whitespace. Does NOT validate email format — call
 * a format check before this (the shared EMAIL_RE in the API + the
 * EMAIL_RE in the SPA invite form).
 */
export function isAdminInviteEmailAllowed(email: string): boolean {
  const normalized = email.trim().toLowerCase();
  const atIndex = normalized.lastIndexOf('@');
  if (atIndex < 0) return false;
  const domain = normalized.slice(atIndex + 1);
  return (ADMIN_INVITE_ALLOWED_DOMAINS as ReadonlyArray<string>).includes(domain);
}
